/**
 * 路由管理
 */

// Vite 插件注入的全局变量
declare const ROUTES_MAP : Record<string, any>;
declare const TABBAR : string[];

// @ts-ignore
import $store from '@/sheep/store';

// @ts-ignore
import { showAuthModal, showShareModal } from '@/sheep/hooks/useModal';

import { isNumber, isString, isEmpty, startsWith, isObject, isNil, clone } from 'lodash-es';

import throttle from '@/sheep/helper/throttle';

/** 路由参数 */
interface RouteParams {
	[key : string] : any;
}

/** 跳转选项 */
interface GoOptions {
	redirect ?: boolean;
}

/**
 * 内部跳转函数
 * @param path 路径
 * @param params 参数
 * @param options 选项
 */
const _go = (
	path : string | { url : string; params ?: RouteParams },
	params : RouteParams = {},
	options : GoOptions = {
		redirect: false,
	}
) => {
	let page = '';
	let query = '';
	let url = '';

	if (isString(path)) {
		// 判断跳转类型是 path 还是 http
		if (startsWith(path as string, 'http')) {
			// #ifdef H5
			window.location.href = path as string;
			return;
			// #endif
			// #ifndef H5
			page = `/pages/public/webview`;
			query = `url=${encodeURIComponent(path as string)}`;
			// #endif
		} else if (startsWith(path as string, 'action:')) {
			handleAction(path as string);
			return;
		} else {
			[page, query] = (path as string).split('?');
		}

		if (!isEmpty(params)) {
			const query2 = paramsToQuery(params);
			if (isEmpty(query)) {
				query = query2;
			} else {
				query += '&' + query2;
			}
		}
	}

	if (isObject(path)) {
		page = (path as { url : string }).url;
		if (!isNil((path as { params ?: RouteParams }).params)) {
			query = paramsToQuery((path as { params : RouteParams }).params);
		}
	}

	// @ts-ignore
	const nextRoute = ROUTES_MAP[page];

	if (!nextRoute) {
		console.log(`%c跳转路径参数错误<${page || 'EMPTY'}>`, 'color:red;background:yellow');
		return;
	}

	// 页面登录拦截
	if (nextRoute.meta?.auth && !$store('user').isLogin) {
		showAuthModal();
		return;
	}

	url = page;
	if (!isEmpty(query)) {
		url += `?${query}`;
	}

	// 跳转底部导航
	// @ts-ignore
	if (TABBAR.includes(page)) {
		uni.switchTab({
			url,
		});
		return;
	}

	// 使用redirect跳转
	if (options.redirect) {
		uni.redirectTo({
			url,
		});
		return;
	}

	uni.navigateTo({
		url,
	});
};

/**
 * 限流跳转
 * @param args 参数
 */
function go(
	path : string | { url : string; params ?: RouteParams },
	params ?: RouteParams,
	options ?: GoOptions
) : void {
	throttle(() => {
		_go(path, params, options);
	});
}

/**
 * 参数转查询字符串
 * @param params 参数对象
 */
function paramsToQuery(params : RouteParams) : string {
	if (isEmpty(params)) {
		return '';
	}
	const query : string[] = [];
	for (const key in params) {
		query.push(key + '=' + params[key]);
	}
	return query.join('&');
}

/**
 * 返回上一页
 */
function back() : void {
	// #ifdef H5
	history.back();
	// #endif

	// #ifndef H5
	uni.navigateBack();
	// #endif
}

/**
 * 重定向跳转
 * @param path 路径
 * @param params 参数
 */
function redirect(path : string, params : RouteParams = {}) : void {
	go(path, params, {
		redirect: true,
	});
}

/**
 * 检测是否有浏览器历史
 * @returns 是否有历史记录
 */
function hasHistory() : boolean {
	// #ifndef H5
	const pages = getCurrentPages();
	if (pages.length > 1) {
		return true;
	}
	return false;
	// #endif

	// #ifdef H5
	return !!(history.state as any)?.back;
	// #endif
}

/**
 * 获取当前路由信息
 * @param field 字段名
 * @returns 路由信息
 */
function getCurrentRoute(field = '') : any {
	let currentPage = getCurrentPage() as any;
	// #ifdef MP
	currentPage.$page['route'] = currentPage.route;
	currentPage.$page['options'] = currentPage.options;
	// #endif
	if (field !== '') {
		return currentPage.$page[field];
	} else {
		return currentPage.$page;
	}
}

/**
 * 获取当前页面实例
 * @returns 当前页面
 */
function getCurrentPage() : any {
	const pages = getCurrentPages();
	return pages[pages.length - 1];
}

/**
 * 处理动作
 * @param path 动作路径
 */
function handleAction(path : string) : void {
	const action = path.split(':');
	switch (action[1]) {
		case 'showShareModal':
			showShareModal();
			break;
	}
}

/**
 * 跳转到错误页面
 * @param errCode 错误码
 * @param errMsg 错误信息
 */
function error(errCode : string, errMsg = '') : void {
	redirect('/pages/public/error', {
		errCode,
		errMsg,
	});
}

export default {
	go,
	back,
	hasHistory,
	redirect,
	getCurrentPage,
	getCurrentRoute,
	error,
};